import {
  ACTION_TYPE,
  BUTTON_ACTION_TYPE,
  BUTTON_TYPES,
  COMPONENT_TYPE,
  INPUT_COMPONENT_TYPE,
  MODULE_TYPE,
  PreviousAction
} from '@abiglobalhealth/go-types'
import { Button } from '@components/Button'
import { AddAttachment } from '@components/icons/AddAttachment'
import { PdfIcon } from '@components/icons/PdfIcon'
import { Input } from '@components/Input'
import imageCompression from 'browser-image-compression'
import { ChangeEventHandler, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { uploadAttachment } from '@/api/consultations'
import PlaneIcon from '@/assets/icons/plane.svg'
import { AttachmentPreview } from '@/components/Consultation/steps/Landing/InputForm/AttachmentPreview'
import { Attachment } from '@/contexts/consultation'
import { useButtonClick } from '@/hooks/useButtonClick'
import { useConsultation } from '@/hooks/useConsultation'
import { useConversationSummaryPrefill } from '@/hooks/useConversationSummaryPrefill'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useQuestion } from '@/hooks/useQuestion'
import { useUser } from '@/hooks/useUser'
import i18n from '@/locale/i18n'
import { cn } from '@/utils/tailwind'

import { VALID_MIME_TYPES } from './utils'

const MAX_ATTACHMENT_SIZE = 5 // 5Mb
const MAX_ATTACHMENTS = 6

export const InputForm = ({ inlineActions = false }: { inlineActions?: boolean }) => {
  const { t } = useTranslation()
  const { handleButtonClick } = useButtonClick()
  const { attachments, question, setAttachments } = useQuestion()
  const { user } = useUser()
  const { showLoading, hideLoading } = useLoading()
  const { currentModule } = useModule()
  const { isFollowUp } = useConsultation()
  const { updateSummary, isFetchingSummary } = useConversationSummaryPrefill()
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const minQuestionLength = useMemo(() => {
    // Follow up doesn't require a minimum length
    if (isFollowUp) return undefined

    switch (i18n.language) {
      case 'zh_hk':
      case 'zh_cn':
      case 'zh_sg':
        return 10
      case 'hu':
        return 40
      case 'ja':
        return 25
      default:
        return 50
    }
  }, [isFollowUp, i18n.language])

  const openAttachmentModal = () => {
    setAttachmentError(null)

    const attachmentInput = document.getElementById('attachment-input')

    if (attachmentInput) {
      attachmentInput.click()
    }
  }

  const handleAddAttachment: ChangeEventHandler<HTMLInputElement> = async (event) => {
    showLoading()
    // If no file was selected, just return
    // (This happens when the user cancels the file selection)
    if (!event.target.files || !event.target.files.length) return

    // Check if already are {MAX_ATTACHMENTS} attachments
    if (attachments.length >= MAX_ATTACHMENTS) {
      hideLoading()
      return setAttachmentError(
        t('error.validation.attachment.max', {
          maxAmount: MAX_ATTACHMENTS
        })
      )
    }

    const file = event.target.files[0]

    // Check if file is too big (>{MAX_ATTACHMENT_SIZE}Mb)
    if (file.size > MAX_ATTACHMENT_SIZE * 1024 * 1024) {
      hideLoading()
      return setAttachmentError(
        t('error.validation.attachment.size', {
          maxSize: `${MAX_ATTACHMENT_SIZE}Mb`
        })
      )
    }

    // File can only be VALID_MIME_TYPES
    if (!VALID_MIME_TYPES.includes(file.type)) {
      hideLoading()
      return setAttachmentError(t('error.validation.attachment.type'))
    }

    let compressedFile = file
    if (file.type.includes('image')) {
      compressedFile = await imageCompression(file, {
        maxSizeMB: MAX_ATTACHMENT_SIZE,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      })
    }

    const reader = new FileReader()
    reader.onloadend = async () => {
      // Check if file is already attached
      if (attachments.find((a) => a.preview === reader.result && a.file.type === compressedFile.type)) {
        hideLoading()
        return setAttachmentError(t('error.validation.attachment.duplicated'))
      }

      try {
        const uploaded = await uploadAttachment({
          uniqueId: user?.data?.uniqueId as string,
          file: reader.result as string
        })

        setAttachments((attachments) => [...attachments, { file: compressedFile, preview: reader.result, url: uploaded?.url }])
      } catch (e) {
        console.error(e)
        setAttachmentError(t('error.validation.attachment.upload'))
      } finally {
        hideLoading()
      }
    }

    reader.readAsDataURL(compressedFile)
  }

  const handleRemoveAttachment = (attachment: Attachment) => {
    setAttachments(attachments.filter((a) => a.preview !== attachment.preview))
    setPreviewAttachment(null)
    setAttachmentError(null)
  }

  const openPreviewModal = (attachment: Attachment) => {
    setPreviewAttachment(attachment)
  }

  const createConsultation = async () => {
    if (isFollowUp) {
      // In case of follow up, we just need to create a follow up consultation (no forms)
      return handleButtonClick({
        type: BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM,
        previousActions: [
          {
            action: ACTION_TYPE.CREATE_MEDICAL_FOLLOW_UP_CONSULTATION
          }
        ]
      })
    }

    if (currentModule?.type !== MODULE_TYPE.CONSULTATION_LANDING) throw new Error('Module is not a consultation landing')

    // If no follow up and no form steps, just create a consultation
    const previousActions: PreviousAction[] = currentModule?.buttonAction.previousActions || []
    if (!user?.data.userId) {
      // If no user, create one first
      previousActions.unshift({
        action: ACTION_TYPE.CREATE_USER
      })
    }

    handleButtonClick(currentModule.buttonAction)
  }

  useEffect(() => {
    updateSummary()
  }, [updateSummary])

  return (
    <>
      <Input
        id="question"
        componentType={COMPONENT_TYPE.INPUT}
        tabIndex={0}
        type={INPUT_COMPONENT_TYPE.TEXTAREA}
        placeholder={isFetchingSummary ? 'loading_summary' : 'placeholder.text'}
        minLength={minQuestionLength}
        customTextareaClassName={inlineActions ? 'min-h-[132px]' : undefined}
        required
        textAreaInnerError
        disabled={isFetchingSummary}
      />

      {attachmentError && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-sm text-red-500" data-testid="attachment-error">
            {attachmentError}
          </p>
        </div>
      )}

      <div
        className={cn(
          'no-scrollbar flex w-full overflow-x-auto transition-all',
          inlineActions ? 'gap-2' : 'gap-4',
          (attachments?.length ?? 0) > 0 ? (inlineActions ? 'h-14' : 'mt-4 h-14') : 'h-0',
          inlineActions && (attachments?.length ?? 0) === 0 && 'hidden'
        )}
        data-testid="attachments-carrousel"
      >
        {attachments?.map((attachment) => (
          <button
            key={attachment.file.name}
            onClick={() => openPreviewModal(attachment)}
            className={cn('replay-block animate-fade-in h-full shrink-0', inlineActions ? 'w-[88px]' : 'w-24')}
            id={`file-${attachment.file.name}`}
            data-testid={`file-${attachment.file.name}`}
          >
            {attachment.file.type === 'application/pdf' ? (
              <div className="flex h-full w-full items-center justify-center rounded-md bg-white">
                <PdfIcon customClassName="text-primary" />
              </div>
            ) : (
              <div className="h-full w-full overflow-hidden rounded-md">
                <img src={attachment.preview as string} className="h-full w-full object-cover" />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className={cn('flex gap-4', inlineActions ? 'flex-row' : 'flex-col pt-4')}>
        <input
          type="file"
          className="hidden"
          id="attachment-input"
          accept={VALID_MIME_TYPES.join(',')}
          onChange={handleAddAttachment}
          onClick={(event) => {
            // This resets the input value so we can upload the same file again
            event.currentTarget.value = ''
          }}
          data-testid="attachment-input"
        />

        <Button
          type={inlineActions ? BUTTON_TYPES.OUTLINED : BUTTON_TYPES.SECONDARY}
          customClassName={cn('flex items-center justify-center bg-white', inlineActions ? 'gap-2 text-sm' : 'gap-3')}
          tabIndex={1}
          onClick={openAttachmentModal}
          data-testid="attachment-button"
          hasShadow={!inlineActions}
        >
          {!inlineActions && <AddAttachment customClassName="h-6 w-6" />}
          <p className="text-primary">{t('attachfiles.button')}</p>
        </Button>

        <Button
          custom={inlineActions}
          customClassName={cn('flex items-center justify-center', inlineActions && 'bg-primary text-sm text-white')}
          tabIndex={2}
          onClick={createConsultation}
          disabled={isFetchingSummary || (isFollowUp && !(question.question ?? '').trim())}
          hasShadow={!inlineActions}
        >
          <div className="flex gap-3">
            <img src={PlaneIcon} className="aspect-square w-6" />
            <p className="text-white">{t('button.send')}</p>
          </div>
        </Button>
      </div>

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onRemove={() => {
            handleRemoveAttachment(previewAttachment)
          }}
        />
      )}
    </>
  )
}
